import { useSuspenseQuery, skipToken } from "@apollo/client";
import { Box, CircularProgress, Skeleton } from "@mui/material";
import { Stack } from "@mui/system";
import { gql } from "generated-graphql";
import { WasteCodesQuery, WasteCodesQueryVariables } from "generated-graphql/graphql";
import { Suspense, useMemo } from "react";
import { WasteCodesContent } from "routes/Customer/Waste/Facilities/Facility/Preview/WasteCodes/Content";

import { useWasteFacilityState } from "../WasteFacilityContext";

const WASTE_CODES = gql(`
  query WasteCodes($facilityId: ID!, $startDate: Date, $endDate: Date, $view: FacilityWasteView, $tags: [String!]) {
    facility(id: $facilityId) {
      id
      wasteCodes(startDate: $startDate, endDate: $endDate, view: $view, tags: $tags) {
        federalWasteCodes
        stateWasteCodes
      }
    }
  }
`);

interface WasteCodesProps {
  facilityId: string;
}

const WasteCodesInner = ({ facilityId }: WasteCodesProps) => {
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const options = useMemo(() => {
    if (!facilityId) {
      return skipToken;
    }
    return {
      variables: {
        facilityId,
        startDate: deferredFilters.startDate,
        endDate: deferredFilters.endDate,
        view: deferredFilters.view,
        tags: deferredFilters.tags,
      },
    };
  }, [deferredFilters, facilityId]);

  const { data, error } = useSuspenseQuery<WasteCodesQuery, WasteCodesQueryVariables>(
    WASTE_CODES,
    options
  );

  return (
    <Suspense
      fallback={
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      }
    >
      <WasteCodesContent data={data?.facility?.wasteCodes ?? undefined} error={error} />
    </Suspense>
  );
};

export const WasteCodes = ({ facilityId }: WasteCodesProps) => {
  return (
    <Suspense
      fallback={
        <Stack spacing={2} width="100%">
          <Skeleton variant="rectangular" width="100%" height={60} sx={{ bgcolor: "grey.300" }} />
          <Skeleton variant="rectangular" width="100%" height={450} sx={{ bgcolor: "grey.300" }} />
        </Stack>
      }
    >
      <WasteCodesInner facilityId={facilityId} />
    </Suspense>
  );
};
